import { useMemo } from 'react'

import { AutocompleteRenderOptionState, TextField, Autocomplete } from '@mui/material'
import { Control, Controller } from 'react-hook-form'

const AutocompleteWrapper = ({ value, onChange, options, children, ...otherProps }) => {
  const selectedOption = useMemo(() => {
    const foundOption = options.find((option) => String(option.value) === String(value))

    // Create a display-only option for database values not in the current options list
    if (!foundOption && value != null && value !== '') {
      return {
        value: value,
        label: String(value), // Convert value to display text
        isNonSelectable: true, // Mark as display-only option
      }
    }

    return foundOption || null
  }, [options, value])

  return children({
    ...otherProps,
    onChange: (_, data) => {
      if (data && !data.isNonSelectable) {
        onChange(data?.value)
      } else if (data === null) {
        onChange(null)
      }
    },
    value: selectedOption,
  })
}

interface IAutocompleteOption {
  value: any
  label: string
  tag?: string
  disabled?: boolean
  isNonSelectable?: boolean
}

type IAutocompleteOptions = Array<IAutocompleteOption>

interface IProps {
  control: Control<any, any>
  label: string
  name: string
  options: IAutocompleteOptions
  sortOptions?: boolean
  defaultValue?: any
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  loading?: boolean
  disableClearable?: boolean
  placeholder?: string
  error?: any
  helperText?: any
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: IAutocompleteOption,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode
}

const ControlledAutocomplete = (props: IProps) => {
  const {
    control,
    label,
    name,
    options,
    sortOptions,
    defaultValue,
    required,
    disabled,
    readOnly,
    loading,
    disableClearable,
    placeholder,
    error,
    helperText,
    renderOption,
  } = props

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { ref, ...restField } }) => (
        <AutocompleteWrapper {...restField} options={options}>
          {(props) => (
            <Autocomplete
              {...props}
              options={
                sortOptions ? options.sort((a, b) => a.label.localeCompare(b.label)) : options
              }
              loading={loading}
              getOptionLabel={(option: any) => option?.label || ''}
              getOptionDisabled={(option: any) => option?.disabled || option?.isNonSelectable}
              isOptionEqualToValue={(option: any, optionValue: any) =>
                option?.value === optionValue?.value
              }
              disableClearable={disableClearable}
              disabled={disabled}
              readOnly={readOnly}
              freeSolo={false}
              filterOptions={(options: IAutocompleteOption[], { inputValue }) => {
                return options.filter(
                  (option) =>
                    !option.isNonSelectable &&
                    option.label.toLowerCase().includes(inputValue.toLowerCase()),
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='outlined'
                  label={label}
                  required={required}
                  placeholder={placeholder}
                  error={error}
                  helperText={helperText}
                />
              )}
              renderOption={
                renderOption ??
                ((props: React.HTMLAttributes<HTMLLIElement>, option: IAutocompleteOption) => {
                  return (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )
                })
              }
            />
          )}
        </AutocompleteWrapper>
      )}
    />
  )
}

export { ControlledAutocomplete }
export type { IAutocompleteOption, IAutocompleteOptions }
